"use client";

import { Pin } from "lucide-react";
import Image from "next/image";
import { type CSSProperties, type ReactNode, useMemo } from "react";
import { cn } from "../../lib";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { RippleButton } from "../material-ui/ripple-button";
import {
	OctagonButtonBottomLabel,
	resolveOctagonButtonHostSizeStyle,
} from "./octagon-button-bottom-label";
import {
	OctagonButtonChrome,
	type OctagonButtonHoverVariant,
} from "./octagon-button-chrome";
import {
	buildOctagonRippleClipStyle,
	OCTAGON_BUTTON_DEFAULT_ICON_PX,
	OCTAGON_BUTTON_DEFAULT_SIZE_PX,
} from "./octagon-geometry";

export const OCTAGON_BUTTON_RIPPLE_BUTTON_CLASSNAME = cn(
	"bg-transparent p-0 shadow-none ring-0 border-0 outline-none",
	"focus-visible:ring-2 focus-visible:ring-slate-950/35 dark:focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40",
);

export interface OctagonButtonProps {
	size?: number;
	iconSize?: number;
	iconSrc?: string;
	icon?: ReactNode;
	badge?: string;
	/** @deprecated Use `badge` — kept for battle thumb tab labels. */
	label?: string;
	badgeAlignBottomToHubOfRingPx?: number;
	/** Extra px downward from hub-aligned badge bottom (positive = lower). */
	badgeOffsetDownPx?: number;
	interactive?: boolean;
	active?: boolean;
	disabled?: boolean;
	onClick?: () => void;
	ariaLabel?: string;
	className?: string;
	style?: CSSProperties;
	zIndex?: number;
	hoverVariant?: OctagonButtonHoverVariant;
	/** Mirror icon horizontally (e.g. flee arrow pointing off-map). */
	iconFlipHorizontal?: boolean;
	/**
	 * Optional top-right overhang chip (count / timer / lock).
	 * Battle wires a shared corner-status badge; other callers may pass any node.
	 */
	cornerStatus?: ReactNode;
	/**
	 * When true, shows a pin badge centered on the top edge (menu lock).
	 * Same chrome language as battle boost large-circle target pin.
	 */
	showPin?: boolean;
	/**
	 * Preferred tip for this control. When disabled, wrap in a focusable host
	 * so the tip still opens (native disabled buttons ignore hover).
	 */
	tooltip?: ReactNode;
}

/**
 * Interactive octagon control — chrome sits outside the clipped ripple host so tile drop-shadow is visible.
 */
export function OctagonButton({
	size = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
	iconSize = OCTAGON_BUTTON_DEFAULT_ICON_PX,
	iconSrc,
	icon,
	badge,
	label,
	badgeAlignBottomToHubOfRingPx,
	badgeOffsetDownPx = 0,
	interactive = true,
	active = false,
	disabled = false,
	onClick,
	ariaLabel,
	className,
	style,
	zIndex,
	hoverVariant = "brightness",
	iconFlipHorizontal = false,
	cornerStatus = null,
	showPin = false,
	tooltip = null,
}: OctagonButtonProps) {
	const badgeText = badge ?? label;
	const isDisabled = disabled || !interactive;
	const isInteractive = !isDisabled && onClick != null;
	const rippleClip = useMemo(() => buildOctagonRippleClipStyle(size), [size]);
	const pinBadgePx = Math.round(Math.min(22, Math.max(16, size * 0.36)));
	const pinIconPx = Math.round(pinBadgePx * 0.5);

	const chrome = (
		<div
			className="pointer-events-none absolute inset-0 overflow-visible"
			aria-hidden
		>
			<OctagonButtonChrome
				size={size}
				interactive={interactive}
				active={active}
				disabled={disabled}
				hoverVariant={hoverVariant}
				className="size-full"
			/>
		</div>
	);

	const iconContent =
		icon ??
		(iconSrc != null ? (
			<Image
				src={iconSrc}
				alt=""
				width={iconSize}
				height={iconSize}
				className={cn(
					"object-contain",
					iconFlipHorizontal && "-scale-x-100",
				)}
				style={{ width: iconSize, height: iconSize }}
				sizes={`${iconSize}px`}
			/>
		) : null);

	const iconSlot =
		iconContent != null ? (
			<div className="sq-octagon-button-icon relative flex size-full items-center justify-center overflow-hidden">
				{iconContent}
			</div>
		) : null;

	const hostSize = resolveOctagonButtonHostSizeStyle(size);

	const host = (
		<div
			className={cn(
				"sq-octagon-button-host group relative shrink-0 overflow-visible",
				isDisabled && "cursor-not-allowed",
				className,
			)}
			data-host-disabled={isDisabled ? "true" : "false"}
			style={{
				...hostSize,
				zIndex: zIndex ?? (cornerStatus != null ? 30 : undefined),
				...style,
			}}
		>
			{chrome}
			{isInteractive ? (
				<RippleButton
					type="button"
					appearance="plain"
					disabled={disabled}
					whileHover={false}
					whileTap={false}
					onClick={onClick}
					className={cn(
						OCTAGON_BUTTON_RIPPLE_BUTTON_CLASSNAME,
						"absolute inset-0 overflow-visible p-0",
					)}
					style={{
						...hostSize,
						...rippleClip,
					}}
					aria-label={ariaLabel ?? badgeText}
					aria-pressed={showPin || undefined}
				>
					{iconSlot}
				</RippleButton>
			) : (
				iconSlot
			)}
			{showPin ? (
				<span
					className="pointer-events-none absolute left-1/2 top-0 z-[40] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] ring-1 ring-black/15"
					style={{ width: pinBadgePx, height: pinBadgePx }}
					aria-hidden
				>
					<Pin
						className="shrink-0 text-slate-900"
						size={pinIconPx}
						strokeWidth={2.25}
						aria-hidden
					/>
				</span>
			) : null}
			{badgeText != null && badgeText.length > 0 ? (
				<OctagonButtonBottomLabel
					text={badgeText}
					alignBottomToHubOfRingPx={badgeAlignBottomToHubOfRingPx}
					offsetDownPx={badgeOffsetDownPx}
				/>
			) : null}
			{cornerStatus != null ? (
				<div className="pointer-events-none absolute inset-0 z-[90] overflow-visible">
					{cornerStatus}
				</div>
			) : null}
		</div>
	);

	if (tooltip == null || tooltip === "") {
		return host;
	}

	return (
		<ContentTooltip
			{...CONTENT_TOOLTIP_SHORT}
			side="top"
			align="center"
			content={tooltip}
			contentClassName="max-w-xs text-xs"
			asChild
		>
			{isDisabled ? (
				<span className="inline-flex cursor-not-allowed" style={hostSize}>
					{host}
				</span>
			) : (
				host
			)}
		</ContentTooltip>
	);
}

export {
	OCTAGON_BUTTON_BADGE_OFFSET_UP_PX,
	OctagonButtonBottomLabel,
	type OctagonButtonBottomLabelProps,
} from "./octagon-button-bottom-label";
export {
	OctagonButtonChrome,
	type OctagonButtonChromeProps,
	type OctagonButtonHoverVariant,
} from "./octagon-button-chrome";
export * from "./octagon-geometry";
